import { ConfigProvider } from "antd";
import trTR from "antd/locale/tr_TR";
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  buildAntdTheme,
  getPalette,
  cssVars,
  type EdelPalette,
  type ThemeMode,
} from "../../theme";

type ColorModeContextType = {
  mode: ThemeMode;
  setMode: () => void;
  /** Aktif modun ham Edel paleti (surface2, gradientHero, glow, shadow…). */
  palette: EdelPalette;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

/** Bileşenlerde aktif modun Edel paletine ve moduna erişim. */
export const useEdel = () => useContext(ColorModeContext);

const resolveInitialMode = (): ThemeMode => {
  const stored = localStorage.getItem("colorMode");
  if (stored === "light" || stored === "dark") return stored;
  return window?.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(resolveInitialMode);

  // Mod değişiminde: localStorage + body[data-theme] + CSS değişkenleri.
  // CSS değişkenleri theme.css'i (scrollbar, hover, focus halkası) besler.
  useEffect(() => {
    window.localStorage.setItem("colorMode", mode);
    const root = document.body;
    root.dataset.theme = mode;
    const vars = cssVars(getPalette(mode));
    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value);
    }
  }, [mode]);

  const toggleMode = () =>
    setModeState((m) => (m === "light" ? "dark" : "light"));

  const palette = useMemo(() => getPalette(mode), [mode]);
  const antdConfig = useMemo(() => buildAntdTheme(mode), [mode]);

  const contextValue = useMemo(
    () => ({ mode, setMode: toggleMode, palette }),
    [mode, palette]
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ConfigProvider locale={trTR} theme={antdConfig}>
        {children}
      </ConfigProvider>
    </ColorModeContext.Provider>
  );
};
